import { useEffect, useMemo, useRef, useState } from "react";

import { useCmmdForgeRuntimeListSnapshot } from "../../cmmd/cmmdForgeRuntimeListResource";
import {
  seedCurrentForgeWorkspaceId,
  useCurrentForgeWorkspaceState,
} from "../../workspace/currentForgeWorkspaceStore";
import type { ForgeWorkspaceMembership } from "../../workspace/resolveCurrentForgeWorkspace";
import {
  deriveCatalogView,
  resolveWorkspaceSelection,
  runEnsureAndApply,
  runResolveAndApply,
  type FirstRunEnvironmentPhase,
  type FirstRunEnvironmentState,
} from "./useFirstRunEnvironment.logic";

export type {
  FirstRunEnvironmentPhase,
  FirstRunEnvironmentState,
} from "./useFirstRunEnvironment.logic";

/**
 * Wraps a setter so a result captured for an old workspace-switch generation
 * is dropped instead of applied (CodeRabbit review, CMMD-Center/forge#3572).
 * A module-level function, not a closure defined inside the hook, so it adds
 * no cognitive-complexity weight to `useFirstRunEnvironment` itself.
 */
function guardStaleSetter<A extends unknown[]>(
  generationRef: { readonly current: number },
  generation: number,
  setter: (...args: A) => void,
): (...args: A) => void {
  return (...args: A) => {
    if (generationRef.current === generation) setter(...args);
  };
}

export function useFirstRunEnvironment(): FirstRunEnvironmentState {
  const cloudCatalog = useCmmdForgeRuntimeListSnapshot();
  const { workspaceId: currentWorkspaceId, switchGeneration } = useCurrentForgeWorkspaceState();
  const [environmentPhase, setEnvironmentPhase] = useState<FirstRunEnvironmentPhase>("idle");
  const [environmentId, setEnvironmentId] = useState<string | null>(null);
  const [environmentError, setEnvironmentError] = useState<string | null>(null);
  const [pendingOrganizationId, setPendingOrganizationId] = useState<number | null>(null);
  const [pendingWorkspaceLabel, setPendingWorkspaceLabel] = useState<string | null>(null);
  const [workspaceOptions, setWorkspaceOptions] = useState<ReadonlyArray<ForgeWorkspaceMembership>>(
    [],
  );

  const catalogView = useMemo(
    () => deriveCatalogView(cloudCatalog, currentWorkspaceId),
    [cloudCatalog, currentWorkspaceId],
  );
  const { environmentId: catalogEnvironmentId, organizationId: catalogOrganizationId } =
    catalogView;
  const catalogSettled = catalogView.settled;
  const resolveStartedRef = useRef(false);
  const ensureStartedRef = useRef(false);
  // Doubles as the "latest known generation" source for `guardStaleSetter`
  // below (CodeRabbit review, CMMD-Center/forge#3572): a bootstrap or ensure
  // request started for one workspace must not apply its result once the
  // shell has switched to another, and this ref already holds the current
  // value between switches -- the effect below sets it synchronously on
  // every actual change, before any late async continuation could read it.
  const previousSwitchGenerationRef = useRef(switchGeneration);

  // An explicit switch (the shell switcher, never this hook's own confirm)
  // invalidates every value this hook resolved for the old workspace. Keyed
  // on `switchGeneration`, not `currentWorkspaceId` itself, because
  // `seedCurrentForgeWorkspaceId` below also changes `currentWorkspaceId`
  // (the account's first confirm) and must NOT reset the `ensuring` phase it
  // just entered.
  useEffect(() => {
    if (previousSwitchGenerationRef.current === switchGeneration) return;
    previousSwitchGenerationRef.current = switchGeneration;
    resolveStartedRef.current = false;
    ensureStartedRef.current = false;
    setEnvironmentPhase("idle");
    setEnvironmentId(null);
    setEnvironmentError(null);
    setPendingOrganizationId(null);
    setPendingWorkspaceLabel(null);
    setWorkspaceOptions([]);
  }, [switchGeneration]);

  useEffect(() => {
    if (catalogEnvironmentId !== null) {
      setEnvironmentId(catalogEnvironmentId);
      setEnvironmentPhase("ready");
      setEnvironmentError(null);
      return;
    }
    if (!catalogSettled || resolveStartedRef.current) {
      return;
    }
    resolveStartedRef.current = true;
    const generation = previousSwitchGenerationRef.current;
    void runResolveAndApply(
      { catalogOrganizationId, currentWorkspaceId },
      {
        setEnvironmentPhase: guardStaleSetter(
          previousSwitchGenerationRef,
          generation,
          setEnvironmentPhase,
        ),
        setPendingOrganizationId: guardStaleSetter(
          previousSwitchGenerationRef,
          generation,
          setPendingOrganizationId,
        ),
        setPendingWorkspaceLabel: guardStaleSetter(
          previousSwitchGenerationRef,
          generation,
          setPendingWorkspaceLabel,
        ),
        setWorkspaceOptions: guardStaleSetter(
          previousSwitchGenerationRef,
          generation,
          setWorkspaceOptions,
        ),
        setEnvironmentError: guardStaleSetter(
          previousSwitchGenerationRef,
          generation,
          setEnvironmentError,
        ),
      },
    );
  }, [catalogEnvironmentId, catalogOrganizationId, catalogSettled, currentWorkspaceId]);

  function selectWorkspace(organizationId: number): void {
    const chosen = resolveWorkspaceSelection({
      pendingOrganizationId,
      workspaceOptions,
      organizationId,
    });
    if (!chosen) return;
    setPendingOrganizationId(chosen.id);
    setPendingWorkspaceLabel(chosen.name);
    setWorkspaceOptions([]);
  }

  function ensure(): void {
    if (ensureStartedRef.current || pendingOrganizationId === null) {
      return;
    }
    ensureStartedRef.current = true;
    setEnvironmentPhase("ensuring");
    // The moment this Seat's workspace is actually confirmed — CMMD-Center/
    // forge#3572's "the app writes an explicit value on first confirm or
    // switch" — not before. Resolving to a workspace and showing its name is
    // not yet a commitment; clicking through to create something in it is.
    seedCurrentForgeWorkspaceId(pendingOrganizationId);
    const generation = previousSwitchGenerationRef.current;
    void runEnsureAndApply(pendingOrganizationId, {
      setEnvironmentPhase: guardStaleSetter(
        previousSwitchGenerationRef,
        generation,
        setEnvironmentPhase,
      ),
      setEnvironmentId: guardStaleSetter(previousSwitchGenerationRef, generation, setEnvironmentId),
      setEnvironmentError: guardStaleSetter(
        previousSwitchGenerationRef,
        generation,
        setEnvironmentError,
      ),
    });
  }

  return {
    catalogEnvironmentId,
    environmentError,
    environmentId,
    environmentPhase,
    pendingOrganizationId,
    pendingWorkspaceLabel,
    workspaceOptions,
    selectWorkspace,
    ensure,
  };
}
